/**
 * Pseudorandom number generator wrapper.
 *
 * The default implementation uses `Math.random()`. Tests pass a seeded RNG to
 * get deterministic outcomes. The server uses a cryptographically strong
 * source where it matters (anti-cheat, gacha rolls, payment receipt nonces).
 */

export type RandomSource = () => number;

/** Default RNG — `Math.random`. Good enough for cosmetic client-side variance. */
export const defaultRandom: RandomSource = Math.random;

/**
 * Tiny deterministic PRNG (mulberry32). Use in tests. NOT for security.
 * Reference: https://github.com/bryc/code/blob/master/jshash/PRNGs.md
 */
export function mulberry32(seed: number): RandomSource {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
  };
}

/** Pick an element from a weighted list. Throws if list is empty. */
export function pickWeighted<T extends { readonly weight: number }>(
  items: readonly T[],
  random: RandomSource = defaultRandom,
): T {
  if (items.length === 0) {
    throw new RangeError('pickWeighted: empty list');
  }

  let totalWeight = 0;
  for (const item of items) {
    if (item.weight < 0 || Number.isNaN(item.weight)) {
      throw new RangeError(`pickWeighted: invalid weight ${String(item.weight)}`);
    }
    totalWeight += item.weight;
  }

  if (totalWeight === 0) {
    throw new RangeError('pickWeighted: total weight is zero');
  }

  const roll = random() * totalWeight;
  let cumulative = 0;
  for (const item of items) {
    cumulative += item.weight;
    if (roll < cumulative) {
      return item;
    }
  }

  // Defensive: floating-point edge case where roll === totalWeight
  // exactly. Safe to return the last item.
  return items[items.length - 1]!;
}
